import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useTheme, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

/**
 * SidebarNavItem — Dark sidebar navigation item.
 * Blue accent for active, subtle white-alpha for hover.
 */
export default function SidebarNavItem({
  item,
  collapsed = false,
  onNavigate,
  onPin,
  isPinned = false,
  showPinControl = false,
  badgeCount = 0,
}) {
  const location = useLocation();
  const theme = useTheme();
  const accent = theme.palette.primary.light;
  const [hovered, setHovered] = useState(false);

  const isActive = item.exact
    ? location.pathname === item.path
    : location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path));

  const handleClick = useCallback(() => {
    if (onNavigate) onNavigate(item.path);
  }, [item.path, onNavigate]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  const handlePin = useCallback(
    (e) => {
      e.stopPropagation();
      if (onPin) onPin(item.id);
    },
    [item.id, onPin]
  );

  const pinButton = showPinControl && !collapsed && (
    <IconButton
      size="small"
      onClick={handlePin}
      aria-label={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
      sx={{
        opacity: hovered || isPinned ? 1 : 0,
        transition: 'opacity 150ms ease',
        color: isPinned ? 'rgba(251,191,36,0.8)' : 'rgba(255,255,255,0.3)',
        p: 0.25,
        '&:hover': {
          color: isPinned ? 'rgba(251,191,36,1)' : 'rgba(255,255,255,0.6)',
          bgcolor: 'transparent',
        },
      }}
      tabIndex={-1}
    >
      {isPinned ? <StarIcon sx={{ fontSize: 12 }} /> : <StarBorderIcon sx={{ fontSize: 12 }} />}
    </IconButton>
  );

  const button = (
    <Box
      role="menuitem"
      tabIndex={0}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 1.25,
        px: collapsed ? 0 : 1.25,
        py: 0.75,
        mb: 0.25,
        borderRadius: '8px',
        cursor: 'pointer',
        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.6)',
        // Softer, flatter active surface than the old heavy filled block.
        bgcolor: isActive ? alpha(accent, 0.14) : 'transparent',
        transition: 'background-color 140ms ease, color 140ms ease',
        outline: 'none',
        position: 'relative',
        userSelect: 'none',
        minHeight: 36,
        '&:hover': {
          bgcolor: isActive ? alpha(accent, 0.18) : 'rgba(255,255,255,0.055)',
          color: isActive ? '#ffffff' : 'rgba(255,255,255,0.92)',
        },
        '&:focus-visible': {
          outline: `2px solid ${alpha(accent, 0.6)}`,
          outlineOffset: '1px',
        },
      }}
    >
      {/* Left accent for active */}
      {isActive && !collapsed && (
        <Box
          sx={{
            position: 'absolute',
            left: -2,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: 18,
            borderRadius: '3px',
            bgcolor: accent,
          }}
        />
      )}

      {/* Icon */}
      <Box
        sx={{
          color: isActive ? accent : 'inherit',
          display: 'flex',
          flexShrink: 0,
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {badgeCount > 0 && collapsed ? (
          <Badge
            variant="dot"
            color="error"
            sx={{ '& .MuiBadge-badge': { top: 2, right: 2 } }}
          >
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </Box>

      {/* Label */}
      {!collapsed && (
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: isActive ? 600 : 450,
            letterSpacing: '0.01em',
            lineHeight: 1.3,
            color: 'inherit',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
          }}
        >
          {item.label}
        </Typography>
      )}

      {/* Badge count */}
      {badgeCount > 0 && !collapsed && (
        <Badge
          badgeContent={badgeCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              position: 'relative',
              transform: 'none',
            },
          }}
        />
      )}

      {/* Pin control */}
      {pinButton}
    </Box>
  );

  if (collapsed) {
    return (
      <Tooltip title={item.label} placement="right" arrow>
        {button}
      </Tooltip>
    );
  }

  return button;
}
